import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth, readError } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Building2, ArrowRight, ShieldCheck, LogIn as LogInIcon, UserPlus, MailCheck, Clock, MailWarning } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1674981208693-de5a9c4c4f44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBvZmZpY2UlMjBidWlsZGluZyUyMGZhY2FkZXxlbnwwfHx8fDE3ODQ0MDc0MDB8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, pendingApproval, login, signup, resendConfirmation } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // 'signin' | 'signup'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);   // shown after successful supabase.auth.signUp
  const [signupEmail, setSignupEmail] = useState("");          // remember email for the success panel
  const [signupOpen, setSignupOpen] = useState(false);
  React.useEffect(() => {
    api.get("/public/signup-enabled").then((r) => setSignupOpen(!!r.data?.enabled)).catch(() => setSignupOpen(false));
  }, []);
  React.useEffect(() => { if (!signupOpen) setMode("signin"); }, [signupOpen]);
  // approvalCode is either null | "PENDING_APPROVAL_NEW" | "ACCESS_RESTRICTED"
  const [approvalCode, setApprovalCode] = useState(pendingApproval || null);
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);
  const [resending, setResending] = useState(false);

  // Force-logout banner: if the api interceptor booted the user out for
  // ACCESS_RESTRICTED (Master toggle OFF or night blackout), show the reason
  // on next paint then clear the flag so the toast fires only once. Same
  // treatment for SESSION_REVOKED (device-limit or manual SA revoke).
  React.useEffect(() => {
    try {
      const msg = sessionStorage.getItem("gb_access_restricted_msg");
      if (msg) {
        toast.error(msg, { duration: 8000 });
        sessionStorage.removeItem("gb_access_restricted_msg");
      }
      const revokedMsg = sessionStorage.getItem("gb_session_revoked_msg");
      if (revokedMsg) {
        toast.error(revokedMsg, { duration: 9000 });
        sessionStorage.removeItem("gb_session_revoked_msg");
      }
    } catch { /* ignore */ }
  }, []);

  // Only auto-redirect when the user has EXPLICITLY signed in during this
  // tab-session (see `sessionStorage.gb_just_signed_in`, set by handleLogin).
  // If a session is restored on refresh or their approval status flips in the
  // background, force them to click Sign In again — the CRM never auto-opens.
  const justSignedIn = typeof window !== "undefined" && sessionStorage.getItem("gb_just_signed_in") === "1";
  if (user && justSignedIn) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setEmailNotConfirmed(false);
    try {
      if (mode === "signin") {
        await login(email, password);
        // Mark this tab-session as "just signed in" so the redirect guard on
        // the Login page allows the auto-redirect ONLY on this click, never
        // on a passive session-restore or background approval flip.
        try { sessionStorage.setItem("gb_just_signed_in", "1"); } catch { /* private mode */ }
        toast.success("Welcome back");
        navigate("/");
      } else {
        await signup({ email, password, name });
        setSignupEmail(email);
        setSignupSuccess(true);
        // Clear the form so switching back to "sign in" doesn't autofill their raw password
        setPassword("");
      }
    } catch (err) {
      const msg = readError(err, mode === "signin" ? "Log in failed. Please try again." : "Signup failed. Please try again.");
      const code = err?.code || msg;
      if (code === "PENDING_APPROVAL_NEW" || code === "ACCESS_RESTRICTED") {
        setApprovalCode(code);
        toast.info(code === "ACCESS_RESTRICTED"
          ? "Your account is currently restricted."
          : "Your account is awaiting Super Admin approval.");
      } else if (code === "EMAIL_NOT_CONFIRMED") {
        setEmailNotConfirmed(true);
      } else if (code === "EMAIL_ALREADY_REGISTERED") {
        toast.error("This email is already registered. Try signing in, or use 'Forgot password?'");
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async (which) => {
    const target = (which || email).trim();
    if (!target) { toast.error("Enter your email first"); return; }
    setResending(true);
    try {
      await resendConfirmation(target);
      toast.success("Confirmation email sent again. Please check your inbox (and spam folder).", { duration: 8000 });
    } catch (err) {
      toast.error(readError(err, "Could not resend the confirmation email"));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-background">
      {/* Left: hero */}
      <div className="hidden lg:block lg:col-span-3 relative overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0B192C]/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[#e6c584] via-[#B78E4C] to-[#8f6c30] flex items-center justify-center font-display font-black text-[#0B192C] text-2xl shadow-[0_6px_28px_-6px_rgba(183,142,76,0.65)]">G</div>
              <span className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full bg-emerald-400 border-2 border-[#0B192C]"></span>
            </div>
            <div className="leading-none">
              <div className="font-display font-black text-2xl tracking-tight bg-gradient-to-br from-[#f5e2b1] via-[#e6c584] to-[#B78E4C] bg-clip-text text-transparent">
                Get Best Property
              </div>
              <div className="mt-2 inline-flex items-center gap-2">
                <span className="h-px w-6 bg-gradient-to-r from-transparent to-[#B78E4C]" />
                <span className="text-[10px] font-mono font-bold tracking-[0.45em] text-[#B78E4C]">EST. 2016</span>
                <span className="h-px w-6 bg-gradient-to-l from-transparent to-[#B78E4C]" />
              </div>
            </div>
          </div>

          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/10 text-xs mb-6">
              <ShieldCheck className="h-3.5 w-3.5 text-[#B78E4C]" />
              <span>Enterprise-grade Real Estate CRM</span>
            </div>
            <h1 className="font-display font-black text-5xl leading-[1.05] tracking-tight mb-4" data-testid="login-hero-tagline">
              Every Lead.<br />
              <span className="text-[#B78E4C]">Every follow up.</span><br />
              Brings you closer to<br />
              cracking your Deal.
            </h1>
            <p className="text-slate-300 text-base leading-relaxed">
              Built for tele-calling teams that live and die by their follow-up discipline.
              Track 15 lead states, never miss a callback, and see the whole pipeline at a glance.
            </p>
          </div>

          <div className="text-xs text-slate-400 font-mono">
            © {new Date().getFullYear()} Get Best Property
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#e6c584] via-[#B78E4C] to-[#8f6c30] flex items-center justify-center font-display font-black text-[#0B192C] text-xl shadow-[0_4px_18px_-4px_rgba(183,142,76,0.55)]">G</div>
            <div className="font-display font-black text-lg tracking-tight bg-gradient-to-br from-[#8f6c30] via-[#B78E4C] to-[#e6c584] bg-clip-text text-transparent">Get Best Property</div>
          </div>

          {/* Awaiting approval banners — two flavours */}
          {approvalCode === "PENDING_APPROVAL_NEW" && (
            <div className="mb-6 rounded-lg border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4" data-testid="pending-approval-banner">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-amber-700 dark:text-amber-400 mt-0.5" />
                <div>
                  <div className="font-semibold text-amber-800 dark:text-amber-300">Awaiting Super Admin approval</div>
                  <p className="text-sm text-amber-800/90 dark:text-amber-300/90 mt-1">
                    Your account is created but hasn{"\u2019"}t been activated yet. A Super Admin will approve it shortly — you{"\u2019"}ll be able to sign in as soon as they do.
                  </p>
                </div>
              </div>
            </div>
          )}

          {approvalCode === "ACCESS_RESTRICTED" && (
            <div className="mb-6 rounded-lg border-2 border-red-300 bg-red-50 dark:bg-red-950/30 p-4" data-testid="access-restricted-banner">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-red-700 dark:text-red-400 mt-0.5" />
                <div>
                  <div className="font-semibold text-red-800 dark:text-red-300">Awaiting Super Admin approval</div>
                  <p className="text-sm text-red-800/90 dark:text-red-300/90 mt-1">
                    Your account is currently inactive/Restricted and requires approval from a Super Admin before you can access the CRM. Once your account is approved, you{"\u2019"}ll be able to sign in and start using the platform.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Email not confirmed banner (shown when the user tries to sign in before clicking the link) */}
          {emailNotConfirmed && !signupSuccess && (
            <div className="mb-6 rounded-lg border-2 border-orange-300 bg-orange-50 dark:bg-orange-950/30 p-4" data-testid="email-not-confirmed-banner">
              <div className="flex items-start gap-3">
                <MailWarning className="h-5 w-5 text-orange-700 dark:text-orange-400 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-orange-800 dark:text-orange-300">Please confirm your email before signing in</div>
                  <p className="text-sm text-orange-800/90 dark:text-orange-300/90 mt-1 leading-relaxed">
                    We sent a confirmation link to <span className="font-mono font-semibold">{email}</span>. Check your inbox (and your spam folder) and click the link to activate your account.
                  </p>
                  <Button
                    type="button"
                    onClick={() => onResend(email)}
                    disabled={resending || !email.trim()}
                    data-testid="resend-confirmation-btn"
                    className="mt-3 h-9 bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60"
                  >
                    {resending ? "Sending…" : "Resend confirmation email"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Signup success — waiting for email confirmation */}
          {signupSuccess ? (
            <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 p-6" data-testid="signup-success-panel">
              <div className="flex items-start gap-3">
                <MailCheck className="h-6 w-6 text-emerald-600 dark:text-emerald-400 mt-0.5" />
                <div className="flex-1">
                  <div className="font-display font-bold text-lg text-emerald-800 dark:text-emerald-200">Check your inbox</div>
                  <p className="text-sm text-emerald-800/90 dark:text-emerald-200/90 mt-1.5 leading-relaxed">
                    We{"\u2019"}ve sent a confirmation link to <span className="font-mono font-semibold">{signupEmail}</span>.
                    Please check your inbox and click the link to activate your account. Once confirmed, a Super Admin will approve your access — you{"\u2019"}ll be signed in only after both steps are complete.
                  </p>
                  <p className="text-xs text-emerald-800/80 dark:text-emerald-200/80 mt-2">
                    Not seeing the email? Check your spam folder, or use the button below to send it again.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      onClick={() => onResend(signupEmail)}
                      disabled={resending}
                      data-testid="signup-resend-btn"
                      variant="outline"
                      className="h-10 border-emerald-400 text-emerald-800 hover:bg-emerald-100 dark:text-emerald-200"
                    >
                      {resending ? "Sending…" : "Resend confirmation email"}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => { setSignupSuccess(false); setMode("signin"); setPassword(""); setEmail(signupEmail); }}
                      data-testid="signup-back-to-signin"
                      className="h-10 bg-[#0B192C] hover:bg-[#0B192C]/90 text-white"
                    >
                      Back to sign in
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="font-display font-bold text-3xl mb-2">
                  {mode === "signin" ? "Log in" : "Create account"}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {mode === "signin"
                    ? "Welcome back. Enter your credentials to access the CRM."
                    : "Register — a confirmation email will be sent, then a Super Admin activates your access."}
                </p>
              </div>

              {signupOpen && <div className="inline-flex bg-secondary rounded-lg p-1 mb-6 w-full" data-testid="login-mode-tabs">
                <button
                  type="button"
                  onClick={() => { setMode("signin"); setApprovalCode(null); setEmailNotConfirmed(false); }}
                  data-testid="login-tab-signin"
                  className={`flex-1 h-9 px-4 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition ${
                    mode === "signin" ? "gb-tab-active" : "gb-tab-inactive"
                  }`}
                >
                  <LogInIcon className="h-4 w-4" /> Log in
                </button>
                <button
                  type="button"
                  onClick={() => { setMode("signup"); setApprovalCode(null); setEmailNotConfirmed(false); }}
                  data-testid="login-tab-signup"
                  className={`flex-1 h-9 px-4 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition ${
                    mode === "signup" ? "bg-[#B78E4C] text-white shadow" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <UserPlus className="h-4 w-4" /> Sign up
                </button>
              </div>}

              <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                {mode === "signup" && (
                  <div className="space-y-2">
                    <Label htmlFor="name">Full name</Label>
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="signup-name"
                      placeholder="e.g. Priya Sharma"
                      className="h-11"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email"
                    required
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">
                    Password{mode === "signup" && <span className="text-xs text-muted-foreground ml-1">(min 6 chars)</span>}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password"
                    required
                    minLength={mode === "signup" ? 6 : undefined}
                    className="h-11"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid={mode === "signin" ? "login-submit" : "signup-submit"}
                  className={`w-full h-11 text-white active:scale-[0.98] transition-transform ${
                    mode === "signin" ? "bg-[#0B192C] hover:bg-[#0B192C]/90" : "bg-[#B78E4C] hover:bg-[#a97e3a]"
                  }`}
                >
                  {submitting ? (mode === "signin" ? "Logging in..." : "Creating account...") : (
                    <span className="flex items-center gap-2">
                      {mode === "signin" ? "Log in" : "Create account"}
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </Button>

                {mode === "signin" && (
                  <div className="text-center">
                    <Link
                      to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`}
                      data-testid="login-forgot-password"
                      className="text-xs text-muted-foreground hover:text-[#B78E4C] underline underline-offset-2"
                    >
                      Forgot password?
                    </Link>
                  </div>
                )}

                {mode === "signup" && (
                  <p className="text-xs text-muted-foreground text-center">
                    You{"\u2019"}ll receive an email to confirm your address. After confirmation, a Super Admin must approve your access before you can sign in.
                  </p>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
